// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// A typography finding drawn inside the card of the finding on its spot
// (`pinGroups.attachCompanions`): one place to fix, so one card and one pin,
// with the companion's own fix and its own judgment.
import { ArrowElbowDownRight } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import cx from "classnames";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCommentJudgmentActions } from "./AskV2ReviewCommentJudgmentActions";
import { AskV2ReviewRejectScopePrompt } from "./AskV2ReviewRejectScopePrompt";
import { pinBlockOf } from "./pinGroups";
import {
  citedRuleBrand,
  citedRuleScopeChoices,
  type RejectionScope,
} from "./rememberedSentence";
import { type CommentWithOptionalTags } from "./reviewCommentTags";

import {
  reviewCommentCollapsedSections,
  reviewCommentTitle,
} from "../../components/ask-puntt-review";
import { reviewCommentGroup } from "../../utils/visibleReviewComments";

/** What the companion asks for: its title, else its Fix line, else its first line. */
function companionFix(comment: CommentWithOptionalTags): string {
  if (comment.title?.trim()) return comment.title.trim();
  const fix = reviewCommentCollapsedSections(comment.description).find(
    (section) => section.label.toLowerCase() === "fix",
  );
  return fix?.body.trim() || reviewCommentTitle(comment);
}

export function AskV2ReviewCompanionRow({
  comment,
  rejecting,
  judgmentPending,
  onRejectingChange,
  onJudge,
}: {
  comment: CommentWithOptionalTags;
  /** Whether this companion's delete opened the reject-scope prompt. */
  rejecting: boolean;
  judgmentPending: boolean;
  onRejectingChange: (commentId: string | null) => void;
  onJudge?: (
    commentId: string,
    liked: boolean | null,
    scope?: RejectionScope,
  ) => void;
}) {
  const { t } = useTranslation("ui");
  const id = comment._id;
  const label = t("askV2.review.companion.sameSpot", {
    block: t(`askV2.review.pinGroups.block.${pinBlockOf(comment)}`),
  });
  const canJudge =
    onJudge != null && comment.isAI && !comment.isPending && !comment.isDeleted;
  const rejected = reviewCommentGroup(comment) === "rejected";
  return (
    <div
      role="group"
      aria-label={label}
      className={cx(
        "border-t border-dashed border-puntt-neutral-gray-6 px-3 py-2",
        // why separate: a leading space in one class string is trimmed by the formatter, which glued
        // this onto "py-2" and a rejected companion never washed out.
        comment.liked === false && "opacity-60",
      )}
      data-testid={`ask-v2-review-comment-companion-${id}`}
    >
      <div className="flex items-start gap-2">
        <ArrowElbowDownRight
          size={14}
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-puntt-neutral-gray-9"
        />
        <div className="min-w-0 flex-1">
          <Text
            as="p"
            size="1"
            weight="medium"
            className="text-puntt-neutral-gray-10"
          >
            {label}
          </Text>
          <Text
            as="p"
            size="2"
            className="mt-0.5 line-clamp-2 text-puntt-neutral-gray-11"
            data-testid={`ask-v2-review-comment-companion-fix-${id}`}
          >
            {companionFix(comment)}
          </Text>
        </div>
        {canJudge ? (
          <AskV2ReviewCommentJudgmentActions
            commentId={id}
            liked={comment.liked}
            rejected={rejected}
            disabled={judgmentPending}
            pinned
            onJudge={(liked) => {
              onRejectingChange(null);
              onJudge?.(id, liked);
            }}
            onRequestDelete={() => onRejectingChange(id)}
          />
        ) : null}
      </div>
      {canJudge && rejecting ? (
        <AskV2ReviewRejectScopePrompt
          commentId={id}
          findingText={comment.description}
          brand={citedRuleBrand(comment.citations)}
          choices={citedRuleScopeChoices(comment.citations)}
          onConfirm={(scope) => {
            onRejectingChange(null);
            onJudge?.(id, false, scope);
          }}
          onCancel={() => onRejectingChange(null)}
        />
      ) : null}
    </div>
  );
}
